/**
 * Window Action Service
 *
 * Browser-side executor for `window_action` RPA jobs. Polls the backend for
 * pending jobs of type "window_action" and executes them against the live React
 * DOM — capturing screenshots, clicking, scrolling, and typing within open
 * Space UI windows.
 *
 * Runs entirely in the browser (no Electron required). The Electron RPA listener
 * explicitly skips window_action jobs so there is no conflict.
 *
 * Poll interval: 10 seconds while idle (no jobs), 2 seconds while a job is
 * actively executing. This reduces idle backend traffic by 5× compared to the
 * previous fixed 2-second poll.
 */

import { domToPng } from 'modern-screenshot';
import { API_ORIGIN_URL } from '../api/config';
import useSpaceStore from '../stores/spaceStore';
import { executeCameraCapture, executeMicrophoneRecord } from './mediaCommandService';

// Fast poll while executing a job so the UI stays responsive; slow poll while
// idle so we don't hammer the backend when no window_action work is queued.
const POLL_INTERVAL_IDLE_MS = 10_000;
const POLL_INTERVAL_ACTIVE_MS = 2_000;
const POST_ACTION_DELAY_MS = 300;

let _pollTimer: ReturnType<typeof setTimeout> | null = null;
let _running = false;
let _stopped = false;

// Browser-side screenshot cache: jobId → base64 PNG.
// Populated when a job completes so the WindowActionRenderer can display the
// screenshot without needing to re-fetch it from the backend.
// Capped to MAX_CACHED_SCREENSHOTS to prevent unbounded memory growth.
export const screenshotByJobId = new Map<string, string>();
const MAX_CACHED_SCREENSHOTS = 10;

// ---------------------------------------------------------------------------
// Auth / API helpers
// ---------------------------------------------------------------------------

function getAuthToken(): string | null {
  try {
    return localStorage.getItem('authToken');
  } catch {
    return null;
  }
}

async function apiFetch(path: string, options: RequestInit = {}): Promise<any> {
  const token = getAuthToken();
  const url = `${API_ORIGIN_URL}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

/** Find the primary scrollable container inside a window element. */
function findScrollableElement(root: Element): Element {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  let node = walker.nextNode() as Element | null;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = walker.nextNode() as Element | null;
  }
  return root;
}

// Selectors for actual window content elements, in priority order.
// WindowEntityTabs also puts data-window-id on tiny tab buttons — we must
// never capture those. Target only the real window wrappers.
const WINDOW_CONTENT_SELECTORS = [
  '.focused-window-wrapper', // focused / full-screen mode
  'div.window',              // spatial mode window chrome (includes header + body)
];

/** Resolve the target window element. Uses windowId param or falls back to the active window. */
function resolveWindowElement(windowId?: string): Element | null {
  function findEl(id: string): Element | null {
    for (const sel of WINDOW_CONTENT_SELECTORS) {
      const el = document.querySelector(`${sel}[data-window-id="${id}"]`);
      if (el) return el;
    }
    return null;
  }

  if (windowId) {
    const el = findEl(windowId);
    if (el) return el;
  }
  // Try active window from spaceStore
  const activeId = useSpaceStore.getState().activeWindowId;
  if (activeId) {
    const el = findEl(activeId);
    if (el) return el;
  }
  // Last resort: first actual window content element on screen
  for (const sel of WINDOW_CONTENT_SELECTORS) {
    const el = document.querySelector(`${sel}[data-window-id]`);
    if (el) return el;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Screenshot
// ---------------------------------------------------------------------------

async function captureWindowScreenshot(windowEl: Element): Promise<{ data: string } | { error: string }> {
  await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

  const rect = windowEl.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return { error: `Window element has zero dimensions (${Math.round(rect.width)}×${Math.round(rect.height)}).` };
  }

  try {
    // modern-screenshot uses SVG <foreignObject> so the browser's own layout engine
    // renders the clone — CSS variables, complex layouts, and ProseMirror contenteditable
    // all render correctly. scale=1.0 keeps pixels 1:1 with DOM coordinates for click actions.
    const dataUrl = await domToPng(windowEl as HTMLElement, {
      scale: 1.0,
      backgroundColor: '#ffffff',
      width: rect.width,
      height: rect.height,
    });
    const base64 = dataUrl.replace(/^data:image\/png;base64,/, '');
    if (!base64) return { error: 'modern-screenshot returned an empty image.' };
    return { data: base64 };
  } catch (err: any) {
    console.error('[WindowAction] Screenshot failed:', err);
    return { error: err?.message || String(err) };
  }
}

// ---------------------------------------------------------------------------
// Action executors
// ---------------------------------------------------------------------------

async function executeScreenshot(params: any): Promise<{ success: boolean; result?: any; error?: string }> {
  const windowEl = resolveWindowElement(params.windowId);
  if (!windowEl) return { success: false, error: 'No open window found.' };

  const result = await captureWindowScreenshot(windowEl);
  if ('error' in result) return { success: false, error: `Screenshot capture failed: ${result.error}` };

  return { success: true, result: { screenshot: result.data, message: 'Screenshot captured.' } };
}

async function executeClick(params: any): Promise<{ success: boolean; result?: any; error?: string }> {
  const { coordinate, windowId } = params;
  if (!Array.isArray(coordinate) || coordinate.length < 2) {
    return { success: false, error: 'coordinate [x, y] is required for click.' };
  }

  const windowEl = resolveWindowElement(windowId);
  if (!windowEl) return { success: false, error: 'No open window found.' };

  const rect = windowEl.getBoundingClientRect();
  const [x, y] = coordinate;

  if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
    return { success: false, error: `Coordinate (${x}, ${y}) is outside the window bounds (${Math.round(rect.width)}×${Math.round(rect.height)}). Call window_screenshot first to see the window dimensions.` };
  }

  const vpX = rect.left + x;
  const vpY = rect.top + y;

  const target = document.elementFromPoint(vpX, vpY) as HTMLElement | null;
  if (!target) return { success: false, error: `No element found at (${x}, ${y}).` };

  target.focus?.();
  target.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, clientX: vpX, clientY: vpY }));
  target.dispatchEvent(new MouseEvent('mouseup',   { bubbles: true, cancelable: true, clientX: vpX, clientY: vpY }));
  target.dispatchEvent(new MouseEvent('click',     { bubbles: true, cancelable: true, clientX: vpX, clientY: vpY }));

  await new Promise(resolve => setTimeout(resolve, POST_ACTION_DELAY_MS));

  const screenshotResult = await captureWindowScreenshot(windowEl);
  const screenshot = 'data' in screenshotResult ? screenshotResult.data : undefined;
  const tagInfo = target.tagName.toLowerCase() + (target.className ? `.${String(target.className).split(' ')[0]}` : '');
  return {
    success: true,
    result: { screenshot, message: `Clicked at (${x}, ${y}) — hit <${tagInfo}>` },
  };
}

async function executeScroll(params: any): Promise<{ success: boolean; result?: any; error?: string }> {
  const { scroll_direction = 'down', scroll_amount = 400, windowId } = params;

  const windowEl = resolveWindowElement(windowId);
  if (!windowEl) return { success: false, error: 'No open window found.' };

  const container = findScrollableElement(windowEl);

  // 'instant' ensures scroll finishes before the post-action screenshot fires.
  // 'smooth' would still be animating when we take the screenshot 300ms later.
  if (scroll_direction === 'top') {
    container.scrollTo({ top: 0, behavior: 'instant' });
  } else if (scroll_direction === 'bottom') {
    container.scrollTo({ top: container.scrollHeight, behavior: 'instant' });
  } else {
    container.scrollBy({ top: scroll_direction === 'down' ? scroll_amount : -scroll_amount, behavior: 'instant' });
  }

  await new Promise(resolve => setTimeout(resolve, POST_ACTION_DELAY_MS));

  const screenshotResult = await captureWindowScreenshot(windowEl);
  const screenshot = 'data' in screenshotResult ? screenshotResult.data : undefined;
  return {
    success: true,
    result: { screenshot, message: `Scrolled ${scroll_direction}` },
  };
}

async function executeType(params: any): Promise<{ success: boolean; result?: any; error?: string }> {
  const { text = '', windowId } = params;
  if (!text) return { success: false, error: 'text is required for type action.' };

  const windowEl = resolveWindowElement(windowId);
  if (!windowEl) return { success: false, error: 'No open window found.' };

  const active = document.activeElement as HTMLElement | null;

  if (!active || !windowEl.contains(active)) {
    return { success: false, error: 'No focused element inside the window. Use action="click" to focus an editable area first, then type.' };
  }

  // For contenteditable elements (e.g. ProseMirror docs), execCommand is the most reliable path.
  const isContentEditable = active?.isContentEditable;
  const isInput = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;

  if (isContentEditable) {
    document.execCommand('insertText', false, text);
  } else if (isInput) {
    const start = active.selectionStart ?? active.value.length;
    const end = active.selectionEnd ?? active.value.length;
    active.value = active.value.slice(0, start) + text + active.value.slice(end);
    active.selectionStart = active.selectionEnd = start + text.length;
    active.dispatchEvent(new Event('input', { bubbles: true }));
    active.dispatchEvent(new Event('change', { bubbles: true }));
  } else {
    // Fallback: dispatch keyboard events for each character
    for (const char of text) {
      const target = active ?? document.body;
      target.dispatchEvent(new KeyboardEvent('keydown', { key: char, bubbles: true }));
      target.dispatchEvent(new KeyboardEvent('keypress', { key: char, bubbles: true }));
      target.dispatchEvent(new KeyboardEvent('keyup', { key: char, bubbles: true }));
    }
  }

  await new Promise(resolve => setTimeout(resolve, POST_ACTION_DELAY_MS));

  const screenshotResult = await captureWindowScreenshot(windowEl);
  const screenshot = 'data' in screenshotResult ? screenshotResult.data : undefined;
  return {
    success: true,
    result: { screenshot, message: `Typed: "${text.slice(0, 40)}"` },
  };
}

// Maps common key names to KeyboardEvent key values
const KEY_ALIASES: Record<string, string> = {
  return: 'Enter', enter: 'Enter',
  escape: 'Escape', esc: 'Escape',
  tab: 'Tab',
  backspace: 'Backspace',
  delete: 'Delete', del: 'Delete',
  arrowup: 'ArrowUp', up: 'ArrowUp',
  arrowdown: 'ArrowDown', down: 'ArrowDown',
  arrowleft: 'ArrowLeft', left: 'ArrowLeft',
  arrowright: 'ArrowRight', right: 'ArrowRight',
  home: 'Home', end: 'End',
  pageup: 'PageUp', pagedown: 'PageDown',
  space: ' ',
};

// Maps key names to their KeyboardEvent.code values (physical key identifiers).
// Handlers like ProseMirror check event.code for shortcuts alongside event.key.
const KEY_CODES: Record<string, string> = {
  Enter: 'Enter', Escape: 'Escape', Tab: 'Tab',
  Backspace: 'Backspace', Delete: 'Delete',
  ArrowUp: 'ArrowUp', ArrowDown: 'ArrowDown', ArrowLeft: 'ArrowLeft', ArrowRight: 'ArrowRight',
  Home: 'Home', End: 'End', PageUp: 'PageUp', PageDown: 'PageDown',
  ' ': 'Space',
};

function resolveKeyCombo(combo: string): { key: string; code: string; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; altKey: boolean } {
  const parts = combo.split('+').map(p => p.trim().toLowerCase());
  const ctrlKey  = parts.includes('ctrl') || parts.includes('control');
  const metaKey  = parts.includes('meta') || parts.includes('cmd') || parts.includes('command');
  const shiftKey = parts.includes('shift');
  const altKey   = parts.includes('alt') || parts.includes('option');
  const keyPart  = parts.find(p => !['ctrl', 'control', 'meta', 'cmd', 'command', 'shift', 'alt', 'option'].includes(p)) ?? '';
  const key = KEY_ALIASES[keyPart] ?? (keyPart.length === 1 ? keyPart : keyPart);
  const code = KEY_CODES[key] ?? (key.length === 1 ? `Key${key.toUpperCase()}` : key);
  return { key, code, ctrlKey, metaKey, shiftKey, altKey };
}

async function executeKey(params: any): Promise<{ success: boolean; result?: any; error?: string }> {
  const { text = '', windowId } = params;
  if (!text) return { success: false, error: 'text (key combo) is required for key action.' };

  const windowEl = resolveWindowElement(windowId);
  if (!windowEl) return { success: false, error: 'No open window found.' };

  const target = (document.activeElement as HTMLElement) ?? document.body;
  const { key, code, ctrlKey, metaKey, shiftKey, altKey } = resolveKeyCombo(text);

  const eventInit: KeyboardEventInit = { key, code, ctrlKey, metaKey, shiftKey, altKey, bubbles: true, cancelable: true };
  target.dispatchEvent(new KeyboardEvent('keydown', eventInit));
  target.dispatchEvent(new KeyboardEvent('keyup',   eventInit));

  await new Promise(resolve => setTimeout(resolve, POST_ACTION_DELAY_MS));

  const screenshotResult = await captureWindowScreenshot(windowEl);
  const screenshot = 'data' in screenshotResult ? screenshotResult.data : undefined;
  return {
    success: true,
    result: { screenshot, message: `Pressed key: "${text}"` },
  };
}

// ---------------------------------------------------------------------------
// Job dispatcher
// ---------------------------------------------------------------------------

async function executeWindowActionJob(job: any): Promise<{ success: boolean; result?: any; error?: string; logs: string[] }> {
  const action = job.action as string;
  const params = job.actionParams ?? {};

  console.log(`[WindowAction] Executing action: ${action}`);

  try {
    let outcome: { success: boolean; result?: any; error?: string };

    switch (action) {
      case 'screenshot':       outcome = await executeScreenshot(params); break;
      case 'click':            outcome = await executeClick(params);      break;
      case 'scroll':           outcome = await executeScroll(params);     break;
      case 'type':             outcome = await executeType(params);       break;
      case 'key':              outcome = await executeKey(params);        break;
      case 'camera_capture':   outcome = await executeCameraCapture(job._id, params); break;
      case 'microphone_record': outcome = await executeMicrophoneRecord(job._id, params); break;
      default:
        outcome = { success: false, error: `Unknown window action: "${action}"` };
    }

    return { ...outcome, logs: [] };
  } catch (err: any) {
    console.error(`[WindowAction] Action "${action}" threw:`, err.message);
    return { success: false, error: err.message, logs: [] };
  }
}

// ---------------------------------------------------------------------------
// Poll loop
// ---------------------------------------------------------------------------

async function pollOnce() {
  if (_running) return;

  const token = getAuthToken();
  if (!token) return;

  let pendingJobs: any[];
  try {
    const result = await apiFetch('/api/rpa/pending');
    pendingJobs = (result.jobs || []).filter((j: any) => j.type === 'window_action');
  } catch (err: any) {
    // Network error or not logged in — skip silently
    return;
  }

  if (pendingJobs.length === 0) return;

  const job = pendingJobs[0];
  _running = true;

  try {
    // Claim the job atomically
    let claimedJob: any;
    try {
      claimedJob = await apiFetch(`/api/rpa/claim/${job._id}`, { method: 'POST' });
    } catch (err: any) {
      console.warn(`[WindowAction] Could not claim job ${job._id}:`, err.message);
      return;
    }

    console.log(`[WindowAction] Claimed job ${claimedJob._id} action="${claimedJob.action}"`);

    const result = await executeWindowActionJob(claimedJob);

    // Cache the screenshot browser-side so the renderer can display it.
    // Evict the oldest entry if the cache exceeds the cap.
    const jobScreenshot = result.result?.screenshot as string | undefined;
    if (jobScreenshot) {
      screenshotByJobId.set(claimedJob._id, jobScreenshot);
      if (screenshotByJobId.size > MAX_CACHED_SCREENSHOTS) {
        const oldestKey = screenshotByJobId.keys().next().value as string;
        screenshotByJobId.delete(oldestKey);
      }
    }

    await apiFetch(`/api/rpa/complete/${claimedJob._id}`, {
      method: 'POST',
      body: JSON.stringify({
        status: result.success ? 'done' : 'error',
        result: result.success ? (result.result ?? {}) : null,
        logs: result.logs ?? [],
        error: result.error ?? null,
      }),
    });

    console.log(`[WindowAction] Completed job ${claimedJob._id} success=${result.success}`);
  } finally {
    _running = false;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

async function tick() {
  if (_stopped) return;
  await pollOnce().catch(err => console.error('[WindowAction] Unhandled poll error:', err.message));
  if (!_stopped) {
    // Poll quickly only while a job is actively executing; back off when idle.
    _pollTimer = setTimeout(tick, _running ? POLL_INTERVAL_ACTIVE_MS : POLL_INTERVAL_IDLE_MS);
  }
}

export function startWindowActionService() {
  _stopped = false;
  if (_pollTimer) {
    clearTimeout(_pollTimer);
    _pollTimer = null;
  }

  console.log(`[WindowAction] Starting poll loop (idle=${POLL_INTERVAL_IDLE_MS}ms, active=${POLL_INTERVAL_ACTIVE_MS}ms)`);

  _pollTimer = setTimeout(tick, POLL_INTERVAL_IDLE_MS);
}

export function stopWindowActionService() {
  _stopped = true;
  if (_pollTimer) {
    clearTimeout(_pollTimer);
    _pollTimer = null;
  }
  console.log('[WindowAction] Stopped');
}

/** Immediately cancel the idle timer and run a poll cycle. Used by media tools
 *  to avoid waiting up to POLL_INTERVAL_IDLE_MS after creating an RPA job. */
export function triggerWindowActionPoll() {
  if (_stopped) return;
  if (_pollTimer) {
    clearTimeout(_pollTimer);
    _pollTimer = null;
  }
  _pollTimer = setTimeout(tick, 0);
}
